/**
 * OnboardingProvider — app-wide tour orchestration with cross-route steps.
 *
 * Mount inside Layout (needs router + auth). Renders ProductTour when active.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductTour } from "./ProductTour";
import { getTour, WELCOME_TOUR_ID } from "./tours";
import {
  getTourState,
  isTourFinished,
  markTourCompleted,
  markTourSkipped,
  resetTourCompletion,
  setLastStepIndex,
} from "./tourStorage";

const OnboardingContext = createContext(null);

const TARGET_WAIT_MS = 4000;
const TARGET_POLL_MS = 100;
const AUTO_START_DELAY_MS = 600;

function filterSteps(steps, { hasPermission, flagEnabled }) {
  return (steps || []).filter((step) => {
    if (step.requirePermission && typeof hasPermission === "function") {
      if (!hasPermission(step.requirePermission)) return false;
    }
    if (step.requireFlag && typeof flagEnabled === "function") {
      if (!flagEnabled(step.requireFlag)) return false;
    }
    return true;
  });
}

function pathMatches(route, pathname) {
  if (!route) return true;
  if (pathname === route) return true;
  // Allow nested paths when step targets a section home (e.g. /hub/...)
  if (route !== "/" && pathname.startsWith(`${route}/`)) return true;
  return false;
}

async function waitForSelector(selector, timeoutMs = TARGET_WAIT_MS) {
  if (!selector) return true;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      if (document.querySelector(selector)) return true;
    } catch {
      return false;
    }
    await new Promise((r) => setTimeout(r, TARGET_POLL_MS));
  }
  return false;
}

export function OnboardingProvider({
  children,
  hasPermission,
  flagEnabled,
  onPrepareStep,
  autoStartWelcome = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTourId, setActiveTourId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetReady, setTargetReady] = useState(true);
  const preparingRef = useRef(0);
  const autoStartedRef = useRef(false);
  const [mandatoryOverride, setMandatoryOverride] = useState(null);

  const activeTour = activeTourId ? getTour(activeTourId) : null;
  const mandatory =
    mandatoryOverride !== null
      ? mandatoryOverride
      : Boolean(activeTour?.mandatory);

  const steps = useMemo(() => {
    if (!activeTour) return [];
    return filterSteps(activeTour.steps, { hasPermission, flagEnabled });
  }, [activeTour, hasPermission, flagEnabled]);

  const isActive = Boolean(activeTourId && steps.length > 0);
  const step = steps[stepIndex] || null;

  const closeTour = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(0);
    setTargetReady(true);
    setMandatoryOverride(null);
  }, []);

  const finish = useCallback(() => {
    if (activeTourId) markTourCompleted(activeTourId);
    closeTour();
  }, [activeTourId, closeTour]);

  const skip = useCallback(() => {
    if (mandatory) return;
    if (activeTourId) markTourSkipped(activeTourId, stepIndex);
    closeTour();
  }, [activeTourId, closeTour, stepIndex, mandatory]);

  const handleClose = useCallback(() => {
    if (mandatory) return;
    skip();
  }, [mandatory, skip]);

  const startTour = useCallback(
    (tourId, { resume = false, mandatory: mandatoryOpt, force = false } = {}) => {
      const tour = getTour(tourId);
      if (!tour) return;
      const filtered = filterSteps(tour.steps, { hasPermission, flagEnabled });
      if (filtered.length === 0) return;

      // Avoid resetting an in-progress tour (e.g. page auto-start re-firing on Next).
      if (!force && !resume && activeTourId === tourId) return;

      let startIdx = 0;
      if (resume) {
        const saved = getTourState(tourId).lastStepIndex;
        startIdx = Math.min(Math.max(0, saved), filtered.length - 1);
      }

      setMandatoryOverride(typeof mandatoryOpt === "boolean" ? mandatoryOpt : null);
      setActiveTourId(tourId);
      setStepIndex(startIdx);
      setTargetReady(false);
    },
    [hasPermission, flagEnabled, activeTourId]
  );

  // Replay via Quick start may be dismissed; first-time auto-start uses tour.mandatory.
  const restartTour = useCallback(
    (tourId) => {
      resetTourCompletion(tourId);
      startTour(tourId, { resume: false, mandatory: false, force: true });
    },
    [startTour]
  );

  const goToIndex = useCallback(
    (nextIdx) => {
      if (!activeTourId) return;
      const clamped = Math.max(0, Math.min(nextIdx, steps.length - 1));
      setStepIndex(clamped);
      setLastStepIndex(activeTourId, clamped);
      setTargetReady(false);
    },
    [activeTourId, steps.length]
  );

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    goToIndex(stepIndex + 1);
  }, [stepIndex, steps.length, finish, goToIndex]);

  const prev = useCallback(() => {
    if (stepIndex <= 0) return;
    goToIndex(stepIndex - 1);
  }, [stepIndex, goToIndex]);

  // Prepare route + sidebar + wait for target when step changes
  useEffect(() => {
    if (!isActive || !step) return;

    const token = ++preparingRef.current;
    let cancelled = false;

    (async () => {
      onPrepareStep?.(step);

      if (step.route && !pathMatches(step.route, location.pathname)) {
        navigate(step.route);
      }

      // Allow route paint + section expand
      await new Promise((r) => setTimeout(r, 80));
      if (cancelled || token !== preparingRef.current) return;

      if (step.selector) {
        await waitForSelector(step.selector);
        if (cancelled || token !== preparingRef.current) return;
        // Always ready so Next works even if the spotlight target is late/missing
        setTargetReady(true);
      } else {
        setTargetReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, step, stepIndex, location.pathname, navigate, onPrepareStep]);

  // Auto-start welcome once for first-time users
  useEffect(() => {
    if (!autoStartWelcome || autoStartedRef.current) return;
    if (typeof hasPermission !== "function") return;
    if (isTourFinished(WELCOME_TOUR_ID)) {
      autoStartedRef.current = true;
      return;
    }
    autoStartedRef.current = true;
    const t = window.setTimeout(() => {
      startTour(WELCOME_TOUR_ID, { resume: true });
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [autoStartWelcome, hasPermission, startTour]);

  const value = useMemo(
    () => ({
      isActive,
      activeTourId,
      stepIndex,
      steps,
      step,
      isFirst: stepIndex <= 0,
      isLast: stepIndex >= steps.length - 1,
      targetReady,
      mandatory,
      startTour,
      restartTour,
      next,
      prev,
      skip,
      finish,
      stop: closeTour,
    }),
    [
      isActive,
      activeTourId,
      stepIndex,
      steps,
      step,
      targetReady,
      mandatory,
      startTour,
      restartTour,
      next,
      prev,
      skip,
      finish,
      closeTour,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <ProductTour
        isOpen={isActive}
        steps={steps}
        stepIndex={stepIndex}
        onNext={next}
        onPrev={prev}
        onFinish={finish}
        onSkip={skip}
        onClose={handleClose}
        targetReady={targetReady}
        mandatory={mandatory}
      />
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}

/** Safe hook when provider may be absent (e.g. Help outside Layout — should not happen). */
export function useOnboardingOptional() {
  return useContext(OnboardingContext);
}
